import React, { useState, useEffect } from "react";
import {
    CardGroup,
    Card,
    Row,
    Col,
    Container,
    Pagination,
    Badge
} from "react-bootstrap";
import { Popover, OverlayTrigger, Button } from "react-bootstrap";
import { GrLinkNext } from "react-icons/gr";
import { motion } from "framer-motion";
import { Carousel } from "../components/Carousel";
import { Link } from "react-router-dom";
import { formatCurrency, formatNumber, parseDate } from "../utils/basis";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faRoad,
    faCar,
    faGasPump,
    faTachometerAlt,
    faPalette,
    faCog,
    faCalendar,
    faList,
    faDollarSign,
    faInfo,
    faBolt,
    faOilCan,
    faLeaf,
} from "@fortawesome/free-solid-svg-icons";
import { faHeart as farHeart } from "@fortawesome/free-solid-svg-icons";
import { faHeart as fasHeart } from "@fortawesome/free-solid-svg-icons";

// Helper funktion til at få drivkraft icon
const getPropellantIcon = (propellant) => {
    if (!propellant) return faGasPump;
    
    const normalizedPropellant = propellant.toLowerCase().trim();
    
    if (normalizedPropellant.includes('El') || normalizedPropellant.includes('electric')) {
        return faBolt;
    } else if (normalizedPropellant.includes('Diesel')) {
        return faOilCan;
    } else if (normalizedPropellant.includes('hybrid')) {
        return faLeaf;
    } else if (normalizedPropellant.includes('Bensin') || normalizedPropellant.includes('petrol')) {
        return faGasPump;
    }
    
    return faGasPump;
};

const CarList = ({ cars, wishlistFirst = false }) => {
    const itemsPerPage = 20;
    const [activePage, setActivePage] = useState(1);
    const [wishlist, setWishlist] = useState([]);

    useEffect(() => {
        const storedWishlist = JSON.parse(localStorage.getItem('wishlist')) || [];
        setWishlist(storedWishlist);
    }, []);

    // Få ønskelistebiler der er i filteredData
    const wishlistCars = wishlist.filter(wCar =>
        cars.some(car => car.VehicleId === wCar.VehicleId)
    );

    // Få resten af bilerne
    const otherCars = cars.filter(car =>
        !wishlist.some(wCar => wCar.VehicleId === car.VehicleId)
    );

    // Kombiner ønskeliste først hvis wishlistFirst er true
    const displayCars = wishlistFirst && wishlistCars.length > 0
        ? [...wishlistCars, ...otherCars]
        : cars;

    const totalPages = Math.ceil(displayCars.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setActivePage(pageNumber);
    };

    useEffect(() => {
        setActivePage(1);
    }, [displayCars]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [activePage]);

    // Pagineringselementer
    let items = [];
    for (let number = 1; number <= totalPages; number++) {
        items.push(
            <Pagination.Item
                key={number}
                active={number === activePage}
                onClick={() => handlePageChange(number)}
            >
                {number}
            </Pagination.Item>
        );
    }

    const indexOfLastCar = activePage * itemsPerPage;
    const indexOfFirstCar = indexOfLastCar - itemsPerPage;
    const currentCars = displayCars.slice(indexOfFirstCar, indexOfLastCar);

    function priceDetail(car) {
        return car.PriceType === "Leasing"
            ? "Månedlig leasing ydelse"
            : "Kontantpris";
    }
    
    function PriceBlock({ label, value, children }) {
        return (
            <div className="PriceBlockContainer">
                <div className="PriceLabel">{label}</div>
                <div className="PriceValueContainer">
                    <h3>{formatCurrency(value)}</h3>
                    {children}
                </div>
            </div>
        );
    }

    const toggleWishlist = (e, car) => {
        const updatedWishlist = wishlist.some(item => item.VehicleId === car.VehicleId)
            ? wishlist.filter(item => item.VehicleId !== car.VehicleId)
            : [...wishlist, car];
        
        setWishlist(updatedWishlist);
        localStorage.setItem('wishlist', JSON.stringify(updatedWishlist));
    };

    return (
        <div>
            {wishlistFirst && wishlistCars.length > 0 && (
                <div className="mb-4">
                    <h5 className="text-muted mb-3">⭐ Dine gemte biler ({wishlistCars.length})</h5>
                </div>
            )}

            <Row xs={1} md={2} lg={2} xl={4} className="g-4">
                {currentCars.map((car) => (
                    <Col key={car.VehicleId}>
                        <motion.div initial="hidden" animate="visible">
                            <div
                                className="luxCarCard"
                                style={{ position: "relative" }}
                            >
                                <div className="lux-carousel-container">
                                    {(() => {
                                        const fallback = "https://img.freepik.com/premium-vector/default-image-icon-vector-missing-picture-page-website-design-mobile-app-no-photo-available_87543-11093.jpg?w=1380";

                                        // Hent kandidat-liste fra kendte felter
                                        const rawPics = Array.isArray(car.Pictures) ? car.Pictures
                                            : Array.isArray(car.Images) ? car.Images
                                            : car.Image ? [car.Image]
                                            : [];

                                        // Konverter til streng-URL'er, håndter objekter og filter tomme
                                        const slides = (rawPics || [])
                                            .map(p => {
                                                if (!p) return null;
                                                if (typeof p === 'string') return p;
                                                if (typeof p === 'object') return p.src || p.url || p.path || null;
                                                return null;
                                            })
                                            .filter(Boolean)
                                            .slice(0, 6) // begræns antal
                                            .map((url, index) => ({
                                                src: typeof url === 'string' ? url.replace?.("class=S1600X1600", "class=S640X640") || url : url,
                                                alt: `Billede ${index + 1}`,
                                                className: "lux-slide",
                                            }));

                                        const data = slides.length ? slides : [{ src: fallback, alt: "Ingen billede", className: "lux-slide" }];

                                        // Debug: fjern kommentaren for at se output i konsol
                                        // console.debug('Car', car.VehicleId, 'slides', data);

                                        return (
                                            <div style={{ overflow: 'hidden', borderRadius: 8 }}>
                                                <Carousel
                                                    className="lux-carousel"
                                                    data={data}
                                                    carId={car.VehicleId}
                                                    car={car}
                                                    useGlobalUrl={false}
                                                    style={{ minHeight: 180 }}
                                                />
                                            </div>
                                        );
                                    })()}
                                    {/* <Badge 
                                        bg="transparent" 
                                        className="position-absolute top-0 end-0 m-2"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleWishlist(e, car);
                                        }}
                                        style={{ cursor: 'pointer', zIndex: 10 }}
                                    >
                                        <FontAwesomeIcon 
                                            icon={wishlist.some(item => item.VehicleId === car.VehicleId) ? fasHeart : farHeart} 
                                            size="2x" 
                                            color={wishlist.some(item => item.VehicleId === car.VehicleId) ? "red" : "white"}
                                        />
                                    </Badge> */}
                                </div>
                                
                                <Link
                                    to={
                                        car.DeletedDate
                                            ? "#"
                                            : `/car/${car.VehicleId}`
                                    }
                                    onClick={(e) =>
                                        car.DeletedDate && e.preventDefault()
                                    }
                                    state={{ car: car }}
                                    className={`styledLink ${
                                        car.DeletedDate ? "disabled-link" : ""
                                    }`}
                                >
                                    <Card.Body>
                                        <Card.Title className="d-flex align-items-center justify-content-between">
                                            <h3>
                                                {car.Make} {car.Model}
                                            </h3>

                                            {car.DeletedDate ? (
                                                <span className="bg-danger text-white px-2 py-1 rounded">
                                                    Solgt
                                                </span>
                                            ) : (
                                                <>
                                                    <div className="ms-auto d-flex ">
                                                        <GrLinkNext />
                                                    </div>
                                                </>
                                            )}

                                            {!car.DeletedDate &&
                                                new Date() -
                                                    new Date(car.CreatedDate) <
                                                    14 * 24 * 3600 * 1000 && (
                                                    <div className="StatusTagsContainer">
                                                        <div className="status-tag">
                                                            <span className="status-tag">
                                                                Nyhed
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                        </Card.Title>
                                        <Card.Text className="text-muted variant-ellipsis">
                                            {car.Variant}
                                        </Card.Text>
                                        <div className="scroll-container">
                                            {[
                                                car.Mileage && {
                                                    icon: faRoad,
                                                    label: `${formatNumber(
                                                        car.Mileage
                                                    )} km`,
                                                },
                                                car.Year && {
                                                    icon: faCalendar,
                                                    label: `${car.Year}`,
                                                },
                                                car.GearType && {
                                                    icon: faCog,
                                                    label: car.GearType,
                                                },
                                                car.Propellant && {
                                                    icon: getPropellantIcon(car.Propellant),
                                                    label: car.Propellant,
                                                },
                                                car.KmPerLiter && {
                                                    icon: faTachometerAlt,
                                                    label: `${car.KmPerLiter} Km/L`,
                                                },
                                                car.Color && {
                                                    icon: faPalette,
                                                    label: car.Color,
                                                },
                                            ]
                                                .filter(Boolean)
                                                .map((detail, index) => (
                                                    <div
                                                        key={index}
                                                        className="detail-container"
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={detail.icon}
                                                            className="detail-icon"
                                                        />
                                                        <p>{detail.label}</p>
                                                    </div>
                                                ))}
                                        </div>
                                    </Card.Body>
                                </Link>
                                
                                {!car.DeletedDate && (
                                    <Card.Footer
                                        style={{ minHeight: "100px" }}
                                        className="d-flex align-items-center justify-content-between"
                                    >
                                        {car.DeletedDate ? (
                                            <span></span>
                                        ) : (
                                            <>
                                                <PriceBlock
                                                    label={priceDetail(car)}
                                                    value={car.Price}
                                                />

                                                {car.PaymentPerMonth > 0 && (
                                                    <div className="d-flex align-items-center">
                                                        <PriceBlock
                                                            label="Månedlig ydelse"
                                                            value={
                                                                car.PaymentPerMonth
                                                            }
                                                        />

                                                        <OverlayTrigger
                                                            trigger="click"
                                                            placement="left"
                                                            rootClose
                                                            overlay={
                                                                <Popover
                                                                    id={`popover-${car.VehicleId}`}
                                                                >
                                                                    <Popover.Header as="h3">
                                                                        Santander
                                                                    </Popover.Header>
                                                                    <Popover.Body
                                                                        style={{
                                                                            fontSize:
                                                                                "1.25rem",
                                                                        }}
                                                                    >
                                                                        <div
                                                                            dangerouslySetInnerHTML={{
                                                                                __html: car.DefaultDutyText,
                                                                            }}
                                                                        />
                                                                    </Popover.Body>
                                                                </Popover>
                                                            }
                                                        >
                                                            <Button
                                                                variant="outline-secondary"
                                                                size="sm"
                                                                className="ms-2"
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faInfo
                                                                    }
                                                                    size="xs"
                                                                />
                                                            </Button>
                                                        </OverlayTrigger>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </Card.Footer>
                                )}
                            </div>
                        </motion.div>
                    </Col>
                ))}
            </Row>
            <Row className="mt-3">
                <Pagination
                    variant="outline-info"
                    className="justify-content-center custom-pagination"
                >
                    {items}
                </Pagination>
            </Row>
        </div>
    );
};

export default CarList;
